import { type ReactNode, useMemo, useState } from 'react';

import { Breakpoint, useBreakpoint } from '@genially/design-system';
import { UserAliasAvatar } from '@genially/ui/build/components/UserAliasAvatar/UserAliasAvatar';

import { Size } from './constants';
import {
  IconWrapper,
  PlayerNameLarge,
  PlayerNameMedium,
  PlayerNameParagraph,
  UserAliasAvatarWrapper,
  Wrapper,
} from './Player.styled';

interface PlayerProps {
  name: string;
  size: Size;
  Icon?: ReactNode;
  dangerOnHover?: boolean;
  onClick?: () => void;
  disableEnterAnimation?: boolean;
}

export const Player = ({
  name,
  size,
  onClick: handleClick,
  Icon,
  dangerOnHover = false,
  disableEnterAnimation = false,
}: PlayerProps) => {
  const [isHover, setIsHover] = useState(false);

  const { isDownwards } = useBreakpoint();
  const isMobile = isDownwards(Breakpoint.xs);

  const userAliasHasSpaces = name.includes(' ');
  const isSmall = isMobile || size === Size.Small;

  const PlayerName = useMemo(() => {
    if (isSmall) return PlayerNameParagraph;

    switch (size) {
      case Size.Medium:
        return PlayerNameMedium;
      default:
        return PlayerNameLarge;
    }
  }, [isSmall, size]);

  const handleHover = () => {
    setIsHover(true);
  };

  const handleBlur = () => {
    setIsHover(false);
  };

  return (
    <Wrapper
      onMouseEnter={handleHover}
      onMouseLeave={handleBlur}
      onClick={handleClick}
      $isSmall={isSmall}
      $disableEnterAnimation={disableEnterAnimation}
      data-cy="player-wrapper"
    >
      <UserAliasAvatarWrapper>
        <UserAliasAvatar
          name={name}
          size={isSmall ? UserAliasAvatar.Size.Small : UserAliasAvatar.Size.Medium}
        />

        <IconWrapper>{Icon && isHover && !isMobile && Icon}</IconWrapper>
      </UserAliasAvatarWrapper>

      <PlayerName $hasSpaces={userAliasHasSpaces} $isDanger={dangerOnHover && isHover}>
        {name}
      </PlayerName>

      {Icon && isMobile && Icon}
    </Wrapper>
  );
};

Player.Size = Size;
